import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useVehicles } from '../../../hooks/useVehicles';
import { useLazyImage } from '../../../hooks/useLazyImage';
import { SIMILAR_VEHICLE_FALLBACK_IMAGE } from '../../../lib/imageUtils';
import { MOCK_VEHICLES } from '../../../mocks/mockVehicles';
import type { Vehicle } from '../../../hooks/useVehicles';

interface SimilarVehiclesProps {
  currentVehicle: Vehicle;
}

function SimilarVehicleCard({ vehicle, isSaved, onToggleSave }: {
  vehicle: Vehicle;
  isSaved: boolean;
  onToggleSave: (uuid: string) => void;
}) {
  const { ref, isVisible } = useLazyImage({ rootMargin: '200px' });
  const imgSrc = vehicle.images?.[0] || SIMILAR_VEHICLE_FALLBACK_IMAGE;
  const uuid = vehicle.uuid || String(vehicle.id);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = SIMILAR_VEHICLE_FALLBACK_IMAGE;
  };

  return (
    <div
      ref={ref}
      className="flex-shrink-0 w-80 rounded-xl overflow-hidden group/card transition-all duration-300 hover:-translate-y-1"
      style={{ background: '#2A2318', border: '1px solid rgba(245,200,66,0.15)' }}
    >
      {/* Image */}
      <div className="relative overflow-hidden bg-[#1E1A14]" style={{ width: 320, height: 210 }}>
        {isVisible ? (
          <picture>
            <source
              type="image/webp"
              srcSet={`${imgSrc}&fm=webp&w=320 320w, ${imgSrc}&fm=webp&w=640 640w`}
              sizes="320px"
            />
            <img
              src={imgSrc}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} — similar vehicle at Rockhound Auto`}
              width={320}
              height={210}
              className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
              loading="lazy"
              decoding="async"
              onError={handleImageError}
            />
          </picture>
        ) : (
          <div className="w-full h-full animate-pulse" style={{ background: '#2A2318' }} />
        )}

        {/* Gold top accent line on hover */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#F5C842] opacity-0 group-hover/card:opacity-100 transition-opacity" />

        {/* Gradient overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(30,26,20,0.7) 0%, transparent 50%)' }} />

        {/* Price badge */}
        <div className="absolute bottom-3 left-3">
          <span
            className="px-2.5 py-1 rounded text-sm font-black"
            style={{ background: '#F5C842', color: '#1E1A14', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.04em' }}
          >
            ${vehicle.price.toLocaleString()}
          </span>
        </div>

        {/* Save Button */}
        <button
          onClick={() => onToggleSave(uuid)}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all cursor-pointer hover:scale-110"
          style={{ background: 'rgba(30,26,20,0.75)', border: '1px solid rgba(245,200,66,0.3)', backdropFilter: 'blur(6px)' }}
          aria-label={isSaved ? `Remove ${vehicle.year} ${vehicle.make} ${vehicle.model} from saved` : `Save ${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          aria-pressed={isSaved}
        >
          <i className={`${isSaved ? 'ri-heart-fill' : 'ri-heart-line'} text-base`} style={{ color: isSaved ? '#ef4444' : '#F5C842' }} aria-hidden="true" />
        </button>

        {/* Body type badge */}
        {vehicle.bodyType && (
          <div className="absolute top-3 left-3">
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white/70" style={{ background: 'rgba(30,26,20,0.7)', border: '1px solid rgba(255,255,255,0.15)' }}>
              {vehicle.bodyType}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3
          className="text-lg font-black text-white mb-3 line-clamp-1 leading-tight"
          style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.04em' }}
        >
          {vehicle.year} {vehicle.make} <span style={{ color: '#F5C842' }}>{vehicle.model}</span>
        </h3>

        {/* Specs row */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {[
            { icon: 'ri-dashboard-line', val: `${vehicle.mileage.toLocaleString()} mi` },
            { icon: 'ri-settings-3-line', val: vehicle.transmission },
            { icon: 'ri-steering-2-line', val: vehicle.drivetrain },
            { icon: 'ri-gas-station-line', val: vehicle.fuelType || 'Gas' },
          ].filter(s => s.val).map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
              <i className={`${s.icon} text-xs`} style={{ color: '#D97706' }} />
              <span>{s.val}</span>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px mb-4" style={{ background: 'rgba(245,200,66,0.15)' }} />

        <Link
          to={`/inventory/${uuid}`}
          className="block w-full py-3 text-center text-sm font-black rounded-lg transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
          style={{ background: '#F5C842', color: '#1E1A14', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.08em' }}
        >
          VIEW DETAILS
        </Link>
      </div>
    </div>
  );
}

function SimilarVehicleMobileCard({ vehicle, isSaved, onToggleSave }: {
  vehicle: Vehicle;
  isSaved: boolean;
  onToggleSave: (uuid: string) => void;
}) {
  const { ref, isVisible } = useLazyImage({ rootMargin: '200px' });
  const imgSrc = vehicle.images?.[0] || SIMILAR_VEHICLE_FALLBACK_IMAGE;
  const uuid = vehicle.uuid || String(vehicle.id);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = SIMILAR_VEHICLE_FALLBACK_IMAGE;
  };

  return (
    <div
      ref={ref}
      className="rounded-xl overflow-hidden group/card transition-all duration-300"
      style={{ background: '#2A2318', border: '1px solid rgba(245,200,66,0.15)' }}
    >
      <div className="relative overflow-hidden bg-[#1E1A14]" style={{ aspectRatio: '4/3' }}>
        {isVisible ? (
          <picture>
            <source
              type="image/webp"
              srcSet={`${imgSrc}&fm=webp&w=400 400w, ${imgSrc}&fm=webp&w=800 800w`}
              sizes="(max-width: 640px) 100vw, 50vw"
            />
            <img
              src={imgSrc}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} — similar vehicle at Rockhound Auto`}
              width={400}
              height={300}
              className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
              loading="lazy"
              decoding="async"
              onError={handleImageError}
            />
          </picture>
        ) : (
          <div className="w-full h-full animate-pulse" style={{ background: '#2A2318' }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(30,26,20,0.65) 0%, transparent 50%)' }} />
        <div className="absolute bottom-3 left-3">
          <span className="px-2.5 py-1 rounded text-sm font-black" style={{ background: '#F5C842', color: '#1E1A14', fontFamily: "'Oswald', sans-serif" }}>
            ${vehicle.price.toLocaleString()}
          </span>
        </div>
        <button
          onClick={() => onToggleSave(uuid)}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110"
          style={{ background: 'rgba(30,26,20,0.75)', border: '1px solid rgba(245,200,66,0.3)' }}
          aria-label={isSaved ? `Remove from saved` : `Save vehicle`}
          aria-pressed={isSaved}
        >
          <i className={`${isSaved ? 'ri-heart-fill' : 'ri-heart-line'} text-base`} style={{ color: isSaved ? '#ef4444' : '#F5C842' }} aria-hidden="true" />
        </button>
      </div>
      <div className="p-4">
        <h3
          className="text-base font-black text-white mb-2 line-clamp-1"
          style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.04em' }}
        >
          {vehicle.year} {vehicle.make} <span style={{ color: '#F5C842' }}>{vehicle.model}</span>
        </h3>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          {[
            { icon: 'ri-dashboard-line', val: `${vehicle.mileage.toLocaleString()} mi` },
            { icon: 'ri-settings-3-line', val: vehicle.transmission },
          ].filter(s => s.val).map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
              <i className={`${s.icon} text-xs`} style={{ color: '#D97706' }} />
              <span>{s.val}</span>
            </div>
          ))}
        </div>
        <Link
          to={`/inventory/${uuid}`}
          className="block w-full py-2.5 text-center text-sm font-black rounded-lg transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
          style={{ background: '#F5C842', color: '#1E1A14', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.08em' }}
        >
          VIEW DETAILS
        </Link>
      </div>
    </div>
  );
}

export default function SimilarVehicles({ currentVehicle }: SimilarVehiclesProps) {
  const { vehicles: liveVehicles } = useVehicles();
  const [savedVehicles, setSavedVehicles] = useState<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const tickingRef = useRef(false);

  const allVehicles = liveVehicles && liveVehicles.length > 0 ? liveVehicles : MOCK_VEHICLES;

  const currentId = currentVehicle.uuid || String(currentVehicle.id);
  const currentBodyType = (currentVehicle.bodyType ?? '').toLowerCase();
  const currentPrice = currentVehicle.price;

  const scored = allVehicles
    .filter((v) => {
      const vid = v.uuid || String(v.id);
      return vid !== currentId && v.status === 'available';
    })
    .map((v) => {
      let score = 0;
      const vBodyType = (v.bodyType ?? '').toLowerCase();
      const priceDiff = Math.abs(v.price - currentPrice);
      if (vBodyType && currentBodyType && vBodyType === currentBodyType) score += 4;
      if (v.make === currentVehicle.make) score += 3;
      if (priceDiff < 5000) score += 2;
      else if (priceDiff < 10000) score += 1;
      if (v.drivetrain && currentVehicle.drivetrain && v.drivetrain === currentVehicle.drivetrain) score += 1;
      return { vehicle: v, score };
    })
    .filter(({ score }) => score >= 1)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8)
    .map(({ vehicle }) => vehicle);

  const similarVehicles = scored.length > 0
    ? scored
    : allVehicles
        .filter((v) => {
          const vid = v.uuid || String(v.id);
          return vid !== currentId && v.status === 'available';
        })
        .sort((a, b) => Math.abs(a.price - currentPrice) - Math.abs(b.price - currentPrice))
        .slice(0, 8);

  const getCategoryLink = () => {
    if (currentBodyType.includes('suv') || currentBodyType.includes('crossover')) {
      return { path: '/used-suvs-warrenton-oregon', label: 'VIEW ALL SUVS' };
    }
    if (currentBodyType.includes('truck') || currentBodyType.includes('pickup')) {
      return { path: '/used-trucks-warrenton-oregon', label: 'VIEW ALL TRUCKS' };
    }
    return { path: '/inventory', label: 'VIEW ALL INVENTORY' };
  };

  const categoryLink = getCategoryLink();

  // Batch geometric reads with requestAnimationFrame to prevent forced reflow
  const updateScrollButtons = useCallback(() => {
    if (tickingRef.current) return;
    tickingRef.current = true;

    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (container) {
        const { scrollLeft, scrollWidth, clientWidth } = container;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 2);
      }
      tickingRef.current = false;
    });
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons, { passive: true });
      window.addEventListener('resize', updateScrollButtons, { passive: true });
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, [updateScrollButtons, similarVehicles]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -420 : 420,
      behavior: 'smooth',
    });
    setTimeout(updateScrollButtons, 350);
  };

  const toggleSave = (vehicleUuid: string) => {
    setSavedVehicles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleUuid)) newSet.delete(vehicleUuid);
      else newSet.add(vehicleUuid);
      return newSet;
    });
  };

  if (similarVehicles.length === 0) return null;

  const sectionLabel = (() => {
    if (currentBodyType.includes('suv') || currentBodyType.includes('crossover')) return 'SIMILAR SUVS';
    if (currentBodyType.includes('truck') || currentBodyType.includes('pickup')) return 'SIMILAR TRUCKS';
    if (currentBodyType.includes('sedan')) return 'SIMILAR SEDANS';
    return 'SIMILAR VEHICLES';
  })();

  return (
    <section id="similar-vehicles" style={{ background: '#1E1A14' }}>
      {/* Film strip top */}
      <div className="flex gap-1 px-6 pt-4 opacity-20">
        {[...Array(60)].map((_, i) => (
          <div key={i} className="flex-1 h-2 rounded-sm bg-white/40 border border-white/10" />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            {/* Gold accent bar */}
            <div className="w-1 h-12 rounded-full bg-[#F5C842]" />
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.25em] mb-1" style={{ color: 'rgba(245,200,66,0.6)', fontFamily: "'Oswald', sans-serif" }}>
                ROCKHOUND AUTO PRESENTS
              </p>
              <h2
                className="font-black leading-none"
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                  letterSpacing: '0.06em',
                  color: 'white',
                }}
              >
                {sectionLabel}
              </h2>
            </div>
          </div>

          <Link
            to={categoryLink.path}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-black transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
            style={{
              background: 'rgba(245,200,66,0.12)',
              border: '1px solid rgba(245,200,66,0.35)',
              color: '#F5C842',
              fontFamily: "'Oswald', sans-serif",
              letterSpacing: '0.08em',
            }}
          >
            {categoryLink.label}
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>

        {/* Match label */}
        <div className="flex items-center gap-2 mb-6">
          <i className="ri-film-line text-sm" style={{ color: '#D97706' }} />
          <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {scored.length > 0 ? 'Matched by body type, make & price range' : 'Other vehicles in a similar price range'}
          </span>
          <div className="flex-1 h-px ml-2" style={{ background: 'rgba(245,200,66,0.12)' }} />
        </div>

        {/* Desktop: Horizontal Scrollable Carousel */}
        <div className="hidden lg:block relative">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110 shadow-2xl"
              style={{ background: '#F5C842', color: '#1E1A14', border: '2px solid rgba(245,200,66,0.4)' }}
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-2xl font-black" aria-hidden="true" />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-110 shadow-2xl"
              style={{ background: '#F5C842', color: '#1E1A14', border: '2px solid rgba(245,200,66,0.4)' }}
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-2xl font-black" aria-hidden="true" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            onScroll={updateScrollButtons}
            className="flex gap-5 overflow-x-auto scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            aria-label={`${sectionLabel} carousel`}
          >
            {similarVehicles.map((vehicle) => {
              const uuid = vehicle.uuid || String(vehicle.id);
              return (
                <SimilarVehicleCard
                  key={uuid}
                  vehicle={vehicle}
                  isSaved={savedVehicles.has(uuid)}
                  onToggleSave={toggleSave}
                />
              );
            })}
          </div>
        </div>

        {/* Mobile/Tablet: Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-5">
          {similarVehicles.slice(0, 4).map((vehicle) => {
            const uuid = vehicle.uuid || String(vehicle.id);
            return (
              <SimilarVehicleMobileCard
                key={uuid}
                vehicle={vehicle}
                isSaved={savedVehicles.has(uuid)}
                onToggleSave={toggleSave}
              />
            );
          })}
        </div>

        {/* Mobile View All Link */}
        <div className="mt-8 text-center lg:hidden">
          <Link
            to={categoryLink.path}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-black transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
            style={{
              background: 'rgba(245,200,66,0.12)',
              border: '1px solid rgba(245,200,66,0.35)',
              color: '#F5C842',
              fontFamily: "'Oswald', sans-serif",
              letterSpacing: '0.08em',
            }}
          >
            {categoryLink.label}
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>
      </div>

      {/* Film strip bottom */}
      <div className="flex gap-1 px-6 pb-4 opacity-20">
        {[...Array(60)].map((_, i) => (
          <div key={i} className="flex-1 h-2 rounded-sm bg-white/40 border border-white/10" />
        ))}
      </div>
    </section>
  );
}
